import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AccessLevel, AdminUserRow, TicketRow } from "@owners/shared-types";
import { CLIENT_LEVELS, LEVEL_LABELS, TICKET_STATUS_LABELS } from "@owners/shared-types";
import { num } from "@owners/format";
import { api } from "../api";
import { DocumentImport } from "./DocumentImport";
import { MarketData } from "./MarketData";

interface ClientRow { id: string; name: string; slug: string; meridianEnabled: boolean; hotels: number; users: number }
interface AdminHotel {
  id: string; name: string; code: string | null; booksCode: string | null; rooms: number | null; addressLine: string | null; city: string | null; state: string | null;
  postalCode: string | null; brand: string | null; managementCompany: string | null; gmName: string | null; legalEntity?: string | null; active: boolean;
}

/** Admin panel (canvas artboard 14): client dropdown, users & access, hotels, help requests from every client. */
export function AdminPanel() {
  const clients = useQuery({ queryKey: ["admin-clients"], queryFn: () => api<{ clients: ClientRow[] }>("/api/admin/clients") });
  const [clientId, setClientId] = useState("");
  const [tab, setTab] = useState<"users" | "hotels" | "import" | "market" | "help">("users");
  useEffect(() => {
    if (!clientId && clients.data?.clients[0]) setClientId(clients.data.clients[0].id);
  }, [clients.data, clientId]);
  const tickets = useQuery({ queryKey: ["admin-tickets"], queryFn: () => api<{ tickets: TicketRow[] }>("/api/admin/tickets") });
  const openCount = tickets.data?.tickets.filter((t) => t.status !== "resolved").length ?? 0;
  const client = clients.data?.clients.find((c) => c.id === clientId);
  const [newClient, setNewClient] = useState(false);

  return (
    <>
      <h1>Admin panel</h1>
      <div className="row" style={{ marginBottom: 14 }}>
        <label className="muted" htmlFor="client">CLIENT</label>
        <select id="client" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          {clients.data?.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button className="btn secondary" onClick={() => setNewClient(true)}>+ New client</button>
        {client && !client.meridianEnabled && <span className="muted" style={{ fontSize: 12 }}>Numbers come from Dataroom uploads (no Meridian books)</span>}
      </div>
      {newClient && <NewClientDialog onClose={(id) => { setNewClient(false); if (id) { setClientId(id); setTab("hotels"); } }} />}
      <div className="tabs">
        <button className={tab === "users" ? "on" : ""} onClick={() => setTab("users")}>Users &amp; access</button>
        <button className={tab === "hotels" ? "on" : ""} onClick={() => setTab("hotels")}>Hotels · {num(client?.hotels ?? 0)}</button>
        <button className={tab === "import" ? "on" : ""} onClick={() => setTab("import")}>Document import</button>
        <button className={tab === "market" ? "on" : ""} onClick={() => setTab("market")}>Market data</button>
        <button className={tab === "help" ? "on" : ""} onClick={() => setTab("help")}>Help requests · all clients{openCount ? ` · ${openCount} open` : ""}</button>
      </div>
      {clientId && tab === "users" && <Users clientId={clientId} />}
      {clientId && tab === "hotels" && <Hotels clientId={clientId} meridian={!!client?.meridianEnabled} />}
      {clientId && tab === "import" && <DocumentImport clientId={clientId} />}
      {clientId && tab === "market" && <MarketData clientId={clientId} />}
      {tab === "help" && <HelpRequests tickets={tickets.data?.tickets ?? []} />}
    </>
  );
}

function useHotels(clientId: string) {
  return useQuery({ queryKey: ["admin-hotels", clientId], queryFn: () => api<{ hotels: AdminHotel[] }>(`/api/admin/hotels?clientId=${clientId}`) });
}

function Users({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ["admin-users", clientId], queryFn: () => api<{ users: AdminUserRow[] }>(`/api/admin/users?clientId=${clientId}`) });
  const hotels = useHotels(clientId);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<AdminUserRow | null>(null);
  const hotelName = useMemo(() => new Map(hotels.data?.hotels.map((h) => [h.id, h.name])), [hotels.data]);
  const patch = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) => api(`/api/admin/users/${v.id}`, { method: "PATCH", body: v.body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-users", clientId] }),
  });
  const counts = Object.fromEntries(CLIENT_LEVELS.map((l) => [l, users.data?.users.filter((u) => u.level === l && u.active).length ?? 0]));
  const hotelCount = hotels.data?.hotels.length ?? 0;

  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
        <div className="level-counts">
          {CLIENT_LEVELS.map((l) => (
            <div key={l} className="level-count"><strong>{num(counts[l] ?? 0)}</strong><span>{LEVEL_LABELS[l]}</span></div>
          ))}
        </div>
        <button className="btn" onClick={() => setInviting(true)}>+ Invite user</button>
      </div>
      {patch.error && <p className="error">{(patch.error as Error).message}</p>}
      <table>
        <thead><tr><th>User</th><th>Company</th><th>Access level</th><th>Hotels</th><th>Dataroom</th><th>Last sign-in</th><th /></tr></thead>
        <tbody>
          {users.data?.users.map((u) => (
            <tr key={u.id} style={u.active ? undefined : { opacity: .5 }}>
              <td><strong>{u.name}</strong><div className="muted">{u.email}</div></td>
              <td>{u.company ?? "—"}</td>
              <td>
                {u.locked ? <span title="Admins are added and changed only in code">{LEVEL_LABELS[u.level]} 🔒</span> : (
                  <select value={u.level} disabled={!u.active || patch.isPending}
                    onChange={(e) => patch.mutate({ id: u.id, body: { level: e.target.value } })}>
                    {CLIENT_LEVELS.map((l) => <option key={l} value={l}>{LEVEL_LABELS[l]}</option>)}
                  </select>
                )}
              </td>
              <td>
                {u.locked ? "All clients" : u.allHotels ? `All ${num(hotelCount)} hotels` : u.hotelIds.length === 1 ? hotelName.get(u.hotelIds[0]!) : `${num(u.hotelIds.length)} hotels`}
                {!u.locked && u.active && <> · <button className="btn link" onClick={() => setEditing(u)}>Change</button></>}
              </td>
              <td>{u.dataroom ? "Yes" : "No"}</td>
              <td>{u.lastSignInAt ? new Date(u.lastSignInAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "Never"}</td>
              <td>
                {!u.locked && (
                  <button className={u.active ? "btn danger" : "btn secondary"} disabled={patch.isPending}
                    onClick={() => patch.mutate({ id: u.id, body: { active: !u.active } })}>
                    {u.active ? "Deactivate" : "Reactivate"}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted" style={{ fontSize: 12 }}>
        Changing a user's access level or deactivating them signs them out immediately. Admin logins can't be created or changed here; only the program developer adds them in code. HMA Users get the Dataroom automatically. What each level can open is set by the AM Leveled Access document.
      </p>
      {inviting && <InviteDialog clientId={clientId} hotels={hotels.data?.hotels ?? []} onClose={() => setInviting(false)} />}
      {editing && <HotelsDialog user={editing} hotels={hotels.data?.hotels ?? []} onClose={() => setEditing(null)}
        onSave={(body) => patch.mutateAsync({ id: editing.id, body }).then(() => setEditing(null))} />}
    </div>
  );
}

function HotelPicker({ hotels, all, setAll, ids, setIds }: { hotels: AdminHotel[]; all: boolean; setAll: (b: boolean) => void; ids: Set<string>; setIds: (s: Set<string>) => void }) {
  return (
    <div className="field">
      <label>Hotels</label>
      <label className="row" style={{ color: "var(--ink)" }}><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> All hotels, including ones added later</label>
      {!all && (
        <div className="checklist">
          {hotels.filter((h) => h.active).map((h) => (
            <label key={h.id}>
              <input type="checkbox" checked={ids.has(h.id)} onChange={(e) => {
                const n = new Set(ids); if (e.target.checked) n.add(h.id); else n.delete(h.id); setIds(n);
              }} />
              {h.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function InviteDialog({ clientId, hotels, onClose }: { clientId: string; hotels: AdminHotel[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: "", email: "", company: "", level: "owner" as AccessLevel });
  const [all, setAll] = useState(true);
  const [ids, setIds] = useState<Set<string>>(new Set());
  const invite = useMutation({
    mutationFn: () => api("/api/admin/users", { method: "POST", body: { ...f, clientId, allHotels: all, hotelIds: [...ids] } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-users", clientId] }); qc.invalidateQueries({ queryKey: ["admin-clients"] }); onClose(); },
  });
  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" role="dialog" aria-label="Invite user" onClick={(e) => e.stopPropagation()}>
        <h2>Invite user</h2>
        {(["name", "email", "company"] as const).map((k) => (
          <div className="field" key={k}>
            <label htmlFor={`inv-${k}`}>{k[0]!.toUpperCase() + k.slice(1)}</label>
            <input id={`inv-${k}`} type={k === "email" ? "email" : "text"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
          </div>
        ))}
        <div className="field">
          <label htmlFor="inv-level">Access level</label>
          <select id="inv-level" value={f.level} onChange={(e) => setF({ ...f, level: e.target.value as AccessLevel })}>
            {CLIENT_LEVELS.map((l) => <option key={l} value={l}>{LEVEL_LABELS[l]}</option>)}
          </select>
        </div>
        <HotelPicker hotels={hotels} all={all} setAll={setAll} ids={ids} setIds={setIds} />
        {invite.error && <p className="error">{(invite.error as Error).message}</p>}
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button className="btn secondary" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={!f.name || !f.email || (!all && ids.size === 0) || invite.isPending} onClick={() => invite.mutate()}>Invite</button>
        </div>
      </div>
    </div>
  );
}

function HotelsDialog({ user, hotels, onClose, onSave }: { user: AdminUserRow; hotels: AdminHotel[]; onClose: () => void; onSave: (b: Record<string, unknown>) => Promise<void> }) {
  const [all, setAll] = useState(user.allHotels);
  const [ids, setIds] = useState<Set<string>>(new Set(user.hotelIds));
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="modal-back" onClick={onClose}>
      <div className="modal" role="dialog" aria-label="Change hotels" onClick={(e) => e.stopPropagation()}>
        <h2>Hotels for {user.name}</h2>
        <HotelPicker hotels={hotels} all={all} setAll={setAll} ids={ids} setIds={setIds} />
        {err && <p className="error">{err}</p>}
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button className="btn secondary" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={!all && ids.size === 0}
            onClick={() => onSave({ allHotels: all, hotelIds: [...ids] }).catch((e) => setErr(e.message))}>Save</button>
        </div>
      </div>
    </div>
  );
}

/** Hotels of the picked client: add, edit, deactivate, or paste many from a spreadsheet. */
function Hotels({ clientId, meridian }: { clientId: string; meridian: boolean }) {
  const hotels = useHotels(clientId);
  const [edit, setEdit] = useState<AdminHotel | "new" | null>(null);
  const [paste, setPaste] = useState(false);
  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
        <span className="muted" style={{ fontSize: 12 }}>
          {meridian ? "Books code = the hotel's Meridian code; it links the hotel to the OCI books." : "This client's numbers come from Dataroom uploads."}
        </span>
        <div className="row">
          <button className="btn secondary" onClick={() => setPaste(true)}>Paste from spreadsheet</button>
          <button className="btn" onClick={() => setEdit("new")}>+ Add hotel</button>
        </div>
      </div>
      <table>
        <thead><tr><th>Hotel</th><th>Code</th>{meridian && <th>Books code</th>}<th>City</th><th className="num">Rooms</th><th>Management company</th><th>GM</th><th /></tr></thead>
        <tbody>
          {hotels.data?.hotels.map((h) => (
            <tr key={h.id} style={h.active ? undefined : { opacity: .5 }}>
              <td>{h.name}{!h.active && " (inactive)"}</td><td>{h.code ?? "—"}</td>{meridian && <td>{h.booksCode ?? "—"}</td>}
              <td>{[h.city, h.state].filter(Boolean).join(", ")}</td><td className="num">{num(h.rooms)}</td>
              <td>{h.managementCompany ?? "—"}</td><td>{h.gmName ?? <span className="tbd">TBD</span>}</td>
              <td className="num"><button className="btn link" onClick={() => setEdit(h)}>Edit</button></td>
            </tr>
          ))}
          {hotels.data && !hotels.data.hotels.length && <tr><td colSpan={8} className="muted">No hotels yet — add one or paste a list.</td></tr>}
        </tbody>
      </table>
      {edit && <HotelDialog clientId={clientId} meridian={meridian} hotel={edit === "new" ? null : edit} onClose={() => setEdit(null)} />}
      {paste && <PasteHotelsDialog clientId={clientId} onClose={() => setPaste(false)} />}
    </div>
  );
}

function NewClientDialog({ onClose }: { onClose: (id?: string) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const save = useMutation({
    mutationFn: () => api<{ client: ClientRow }>("/api/admin/clients", { method: "POST", body: { name } }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["admin-clients"] }); onClose(r.client.id); },
  });
  return (
    <div className="modal-back"><div className="modal">
      <h2>New client</h2>
      <p className="muted" style={{ fontSize: 13 }}>The client's people only ever see this client's hotels. Add hotels next, then invite users.</p>
      <div className="field"><label>Client name</label><input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Northstar Hospitality, LLC" /></div>
      {save.error && <p className="error">{(save.error as Error).message}</p>}
      <div className="row"><button className="btn gold" disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>Create</button><button className="btn secondary" onClick={() => onClose()}>Cancel</button></div>
    </div></div>
  );
}

const HOTEL_FORM: Array<[keyof AdminHotel, string]> = [
  ["name", "Hotel name"], ["code", "Code"], ["rooms", "Rooms"], ["brand", "Brand"], ["addressLine", "Address"], ["city", "City"],
  ["state", "State"], ["postalCode", "ZIP"], ["managementCompany", "Management company"], ["gmName", "General manager"],
  ["legalEntity", "Legal entity (owning LLC; ; between several)"],
];

function HotelDialog({ clientId, meridian, hotel, onClose }: { clientId: string; meridian: boolean; hotel: AdminHotel | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<Record<string, string>>(() => {
    const o: Record<string, string> = {};
    for (const [k] of [...HOTEL_FORM, ["booksCode", ""] as [keyof AdminHotel, string]]) o[k] = hotel?.[k] == null ? "" : String(hotel[k]);
    return o;
  });
  const [active, setActive] = useState(hotel?.active ?? true);
  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { ...f, active };
      if (!meridian) delete body.booksCode;
      return hotel ? api(`/api/admin/hotels/${hotel.id}`, { method: "PATCH", body }) : api("/api/admin/hotels", { method: "POST", body: { ...body, clientId } });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-hotels", clientId] }); qc.invalidateQueries({ queryKey: ["admin-clients"] }); onClose(); },
  });
  return (
    <div className="modal-back"><div className="modal">
      <h2>{hotel ? `Edit ${hotel.name}` : "Add hotel"}</h2>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
        {HOTEL_FORM.map(([k, label]) => (
          <div key={k} className="field" style={k === "name" || k === "addressLine" ? { gridColumn: "1 / -1" } : undefined}>
            <label>{label}</label><input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
          </div>
        ))}
        {meridian && <div className="field"><label>Books code (Meridian)</label><input value={f.booksCode} onChange={(e) => setF({ ...f, booksCode: e.target.value })} placeholder="e.g. DALDU" /></div>}
      </div>
      {hotel && <label style={{ fontSize: 13 }}><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active (inactive hotels disappear from every page)</label>}
      {save.error && <p className="error">{(save.error as Error).message}</p>}
      <div className="row" style={{ marginTop: 12 }}><button className="btn gold" disabled={!f.name?.trim() || save.isPending} onClick={() => save.mutate()}>Save</button><button className="btn secondary" onClick={onClose}>Cancel</button></div>
    </div></div>
  );
}

/** Columns pasted from Excel (tab-separated, header row first). Codes match existing hotels, so re-pasting updates. */
const PASTE_COLS: Record<string, string> = {
  name: "name", hotel: "name", "hotel name": "name", code: "code", rooms: "rooms", keys: "rooms", brand: "brand", address: "addressLine",
  city: "city", state: "state", zip: "postalCode", "management company": "managementCompany", "mgmt company": "managementCompany", gm: "gmName", "general manager": "gmName",
  "legal entity": "legalEntity", "legal name": "legalEntity", "owner entity": "legalEntity",
};
export function parsePastedHotels(text: string): { rows: Record<string, string>[]; unknown: string[] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return { rows: [], unknown: [] };
  const head = lines[0]!.split("\t").map((h) => h.trim().toLowerCase());
  const map = head.map((h) => PASTE_COLS[h] ?? null);
  const rows = lines.slice(1).map((l) => {
    const cells = l.split("\t");
    const o: Record<string, string> = {};
    map.forEach((k, i) => { if (k && cells[i]?.trim()) o[k] = cells[i]!.trim(); });
    return o;
  });
  return { rows, unknown: head.filter((_, i) => !map[i]) };
}

function PasteHotelsDialog({ clientId, onClose }: { clientId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const parsed = useMemo(() => parsePastedHotels(text), [text]);
  const save = useMutation({
    mutationFn: () => api<{ rows: number; hotels: number }>("/api/admin/hotels/bulk", { method: "POST", body: { clientId, rows: parsed.rows } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-hotels", clientId] }); qc.invalidateQueries({ queryKey: ["admin-clients"] }); onClose(); },
  });
  return (
    <div className="modal-back"><div className="modal" style={{ width: 640 }}>
      <h2>Paste hotels</h2>
      <p className="muted" style={{ fontSize: 13 }}>Copy rows from Excel with a header row: Hotel, Code, Rooms, Brand, Address, City, State, ZIP, Management company, GM. Hotel and Code are required; pasting again with the same codes updates those hotels.</p>
      <textarea style={{ width: "100%", minHeight: 180, fontFamily: "monospace", fontSize: 12 }} value={text} onChange={(e) => setText(e.target.value)} />
      <p className="muted" style={{ fontSize: 12 }}>{parsed.rows.length} hotel{parsed.rows.length === 1 ? "" : "s"} read{parsed.unknown.length ? ` · ignored columns: ${parsed.unknown.join(", ")}` : ""}</p>
      {save.error && <p className="error">{(save.error as Error).message}</p>}
      <div className="row"><button className="btn gold" disabled={!parsed.rows.length || save.isPending} onClick={() => save.mutate()}>Add / update {parsed.rows.length}</button><button className="btn secondary" onClick={onClose}>Cancel</button></div>
    </div></div>
  );
}

function HelpRequests({ tickets }: { tickets: TicketRow[] }) {
  const qc = useQueryClient();
  const [reply, setReply] = useState<Record<string, string>>({});
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-tickets"] });
  const send = useMutation({
    mutationFn: (id: string) => api(`/api/admin/tickets/${id}/reply`, { method: "POST", body: { body: reply[id] } }),
    onSuccess: (_d, id) => { setReply({ ...reply, [id]: "" }); refresh(); },
  });
  const close = useMutation({
    mutationFn: (id: string) => api(`/api/admin/tickets/${id}`, { method: "PATCH", body: { status: "resolved" } }),
    onSuccess: refresh,
  });
  if (!tickets.length) return <div className="card muted">No help requests.</div>;
  return (
    <div className="card">
      {tickets.map((t) => (
        <div key={t.id} style={{ marginBottom: 18 }}>
          <div className="row">
            <strong>#{t.number} · {t.topic}</strong>
            <span className={`pill ${t.status}`}>{TICKET_STATUS_LABELS[t.status]}</span>
            <span className="muted">{t.userName} · {LEVEL_LABELS[t.userLevel]} · {t.clientName}</span>
          </div>
          <div className="thread">
            {t.messages.map((m) => (
              <div key={m.id} className={`msg ${m.fromAdmin ? "admin" : ""}`}>
                <div className="muted" style={{ fontSize: 12 }}>{m.authorName} · {new Date(m.createdAt).toLocaleString()}</div>
                <div style={{ whiteSpace: "pre-wrap" }}>{m.body}</div>
              </div>
            ))}
          </div>
          {t.status !== "resolved" && (
            <div className="row">
              <input style={{ flex: 1 }} placeholder="Reply" value={reply[t.id] ?? ""} onChange={(e) => setReply({ ...reply, [t.id]: e.target.value })} />
              <button className="btn" disabled={!reply[t.id]?.trim() || send.isPending} onClick={() => send.mutate(t.id)}>Reply</button>
              <button className="btn secondary" disabled={close.isPending} onClick={() => close.mutate(t.id)}>Resolve</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
